import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar, AdminTab } from './admin/Sidebar';
import { UserManagement } from './admin/UserManagement';
import { TopUpApproval } from './admin/TopUpApproval';
import { LibraryManagement } from './admin/LibraryManagement';
import { AdminActivity } from './admin/AdminActivity';
import { AppConfig } from './admin/AppConfig';

export const AdminPortal: React.FC = () => {
  // Helper to determine initial admin tab based on current URL path
  const getTabFromUrl = (): AdminTab => {
    if (typeof window === 'undefined') return 'activity';
    const pathname = window.location.pathname.toLowerCase();

    if (pathname.includes('/admin/topup')) return 'topup';
    if (pathname.includes('/admin/user') || pathname.includes('/admin/users')) return 'user';
    if (pathname.includes('/admin/docs') || pathname.includes('/admin/library')) return 'docs';
    if (pathname.includes('/admin/setting') || pathname.includes('/admin/config') || pathname.includes('/admin/settings')) return 'setting';
    if (pathname === '/admin' || pathname === '/admin/' || pathname.includes('/admin/activity')) return 'activity';

    return 'activity';
  };

  const [activeAdminTab, setActiveAdminTabState] = useState<AdminTab>(getTabFromUrl);

  // Sync tab with browser URL history
  const handleTabChange = useCallback((tab: AdminTab) => {
    const normalizedTab = 
      tab === 'users' ? 'user' :
      tab === 'library' ? 'docs' :
      tab === 'config' ? 'setting' :
      tab;

    setActiveAdminTabState(normalizedTab);

    if (typeof window !== 'undefined') {
      let targetPath = '/admin';
      if (normalizedTab === 'topup') targetPath = '/admin/topup';
      else if (normalizedTab === 'user') targetPath = '/admin/user';
      else if (normalizedTab === 'docs') targetPath = '/admin/docs';
      else if (normalizedTab === 'setting') targetPath = '/admin/setting';
      else targetPath = '/admin';

      if (window.location.pathname !== targetPath || window.location.search) {
        window.history.pushState({ adminTab: normalizedTab }, '', targetPath);
      }
    }
  }, []);

  // Listen to popstate (back/forward navigation)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      const currentTab = getTabFromUrl();
      setActiveAdminTabState(currentTab);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const normalizedTab = 
    activeAdminTab === 'users' ? 'user' :
    activeAdminTab === 'library' ? 'docs' :
    activeAdminTab === 'config' ? 'setting' :
    activeAdminTab;

  return (
    <div className="flex flex-col md:flex-row gap-6 items-start">
      {/* Admin Sidebar Navigation */}
      <Sidebar
        activeAdminTab={normalizedTab}
        setActiveAdminTab={handleTabChange}
      />

      {/* Main Admin Tab View Content */}
      <div className="flex-1 w-full min-w-0 space-y-6">
        {normalizedTab === 'activity' && <AdminActivity />}
        {normalizedTab === 'topup' && <TopUpApproval />}
        {normalizedTab === 'user' && <UserManagement />}
        {normalizedTab === 'docs' && <LibraryManagement />}
        {normalizedTab === 'setting' && <AppConfig />}
      </div>
    </div>
  );
};

export default AdminPortal;
